// @ts-strict
import { Injectable } from '@angular/core';
import { LMContextService, LMLoggerService } from '../services';
import { LMContextUtilService } from '@utils/lm-context-util.service';

export type LMPortalOverlayAutoResizeOptions = {
    debounce?: number; // Number of milliseconds to wait before resizing again
};

const LMPortalOverlayConstants = {
    MIN_WIDTH: 400,
    MIN_HEIGHT: 450
} as const;

@Injectable()
export class LMPortalOverlaySizingService {
    /**
     * Service to handle auto sizing of the hosting iframe inside a Portal Overlay modal dialog.
     * This solution sizes as large as possible to reduce or avoid scrollbars. It only sizes up.
     */
    constructor(
        private lmLoggerService: LMLoggerService,
        private lmContextService: LMContextService,
        private lmContextUtilService: LMContextUtilService) {
    }

    /**
     * Size the hosting iframe to fit the content.
     */
    public autoResize() {
        this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'autoResize' ]);
        const extraWidth = this.horizontalOverflow(document.documentElement);
        const extraHeight = this.verticalOverflow(document.documentElement);
        const isMinSize = window.innerWidth >= LMPortalOverlayConstants.MIN_WIDTH && window.innerHeight >= LMPortalOverlayConstants.MIN_HEIGHT;

        // Ignore small overflow to avoid jumpiness.
        if (extraWidth < 20 && extraHeight < 20 && isMinSize) {
            return;
        }

        this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'Extra width: ' + extraWidth + ', extra height: ' + extraHeight ]);

        // We don't know the Portal window's viewport, so set sane limits to account for browser borders, toolbars, etc.
        // Never size smaller than the incoming size. Max limits may have already been set by the Portal.
        const maxWidth = Math.max(window.outerWidth * 0.7, window.innerWidth);
        const maxHeight = Math.max(window.outerHeight * 0.7, window.innerHeight);

        const width = Math.min(window.innerWidth + extraWidth, maxWidth);
        const height = Math.min(window.innerHeight + extraHeight, maxHeight);

        if (width > window.innerWidth || height > window.innerHeight || !isMinSize) {
            this.setIframeSize(width, height);
        }

        if (width === maxWidth) {
            this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'Iframe is at the maximum width' ]);
        }

        if (height === maxHeight) {
            this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'Iframe is at the maximum height' ]);
        }
    }

    /**
     * Size the hosting iframe to a given width and height.
     */
    public setIframeSize(width: number, height: number) {
        const frameWidth = Math.max(width, LMPortalOverlayConstants.MIN_WIDTH);
        const frameHeight = Math.max(height, LMPortalOverlayConstants.MIN_HEIGHT);
        this.lmContextUtilService.resizeOverlay(frameWidth, frameHeight);
    }

    /**
     * Start listening for DOM changes that may require resizing the iframe.
     * @param debounce The number of milliseconds to wait after the last mutation before resizing again.
     * @returns A function that can be called to stop the auto-resizing.
     */
    public startAutoResizing(options: LMPortalOverlayAutoResizeOptions = {}) {
        const debounce = options.debounce ?? 500;

        this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'Starting auto-resizing' ]);
        let timer: any;
        const observer = new MutationObserver((records: MutationRecord[]) => {
            if (records.some((record: MutationRecord) => record.addedNodes.length )) { // Resize when any nodes are added
                clearTimeout(timer);
                timer = setTimeout(() => {
                    this.autoResize();
                }, debounce);
            }
        });
        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false,
        });
        return () => {
            this.lmLoggerService.log([ 'LMPortalOverlaySizingService', 'Stopping auto-resizing' ]);
            observer.disconnect();
        };
    }

    /**
     * Find how much the element (and its children) overflows horizontally by
     * traversing the DOM tree. Skip elements that are not visible.
     */
    private horizontalOverflow(element: HTMLElement) {
        if (this.hasHorizontalOverflow(element)) {
            const hOverflow = element.scrollWidth - element.clientWidth;
            return hOverflow > 0 ? Math.ceil(hOverflow) + 1 : 0; // add 1px as a safety buffer to avoid scrollbar
        }
        let overflow = 0;
        for (const child of Array.from(element.children) as HTMLElement[]) {
            if (!child.checkVisibility()) {
                continue;
            }
            const childOverflow = this.horizontalOverflow(child);
            if (childOverflow > overflow) {
                overflow = childOverflow;
            }
        }
        return overflow;
    }

    /**
     * Find how much the element (and its children) overflows vertically by
     * traversing the DOM tree. Skip elements that are not visible.
     */
    private verticalOverflow(element: HTMLElement) {
        if (this.hasVerticalOverflow(element)) {
            const vOverflow = element.scrollHeight - element.clientHeight;
            return vOverflow > 0 ? Math.ceil(vOverflow) + 1 : 0; // add 1px as a safety buffer to avoid scrollbar
        }
        let overflow = 0;
        for (const child of Array.from(element.children) as HTMLElement[]) {
            if (!child.checkVisibility()) {
                continue;
            }
            const childOverflow = this.verticalOverflow(child);
            if (childOverflow > overflow) {
                overflow = childOverflow;
            }
        }
        return overflow;
    }

    /**
     * Is the element overflowing horizontally?
     */
    private hasHorizontalOverflow(element: HTMLElement) {
        if (["scroll", "auto"].includes(window.getComputedStyle(element).overflowX) || element === document.documentElement) {
            if (element.tagName === "UL" && element?.parentElement?.classList.contains("dropdown-list")) { // ignore overflow on dropdowns
                return false;
            }
            return (element.clientWidth < element.scrollWidth);
        }
        return false;
    }

    /**
     * Is the element overflowing vertically?
     */
    private hasVerticalOverflow(element: HTMLElement) {
        if (["scroll", "auto"].includes(window.getComputedStyle(element).overflowY) || element === document.documentElement) {
            if (element.tagName === "UL" && element?.parentElement?.classList.contains("dropdown-list")) { // ignore overflow on dropdowns
                return false;
            }
            return (element.clientHeight < element.scrollHeight);
        }
        return false;
    }
}